/* eslint-disable no-unused-vars */
import store from '@/store';
import {
	TAG_COLOR_HEX,
	ForgeAnomaly,
	isScaffoldPlaced
} from './forgeModels';
import { getColorImagePath, getCachedDynamicIcon } from '@/utils';
import { log as debugLog, warn as debugWarn, error as debugError } from '@/utils/debugLogger';
// Wave C-α popup — open the CML popup card on a 360 hotspot click (parity with
// the 3D marker path in forgeService._setupCmlMarkerClickHandler). Safe import:
// usePopupCard depends on neither forgeService nor this manager (no cycle).
import { openPopupCard } from '@/composables/usePopupCard';
import {
	TAG_MARKER_OUTCOME,
	canRenderSelectedPointMarker,
	createPanoramaTagSelectionScope,
	createPanoramaTagSelectionSeed,
} from '@/features/Tags/utils/tagMarkerLifecycle';

const THREE = window.THREE;

// Scaffold, Permit and Container share one marker family discriminated by `type`.
export const SCAFFOLD_FAMILY_TYPES = Object.freeze(['scaffold', 'permit', 'container']);

// Shared chrome for the 360 tag labels. The chip is a NEUTRAL dark overlay, not a
// tint of the tag colour: the icon beside the text is generated in that colour and
// would disappear into a same-coloured chip. Colour semantics therefore live on the
// icon, and the title gets white text plus the 4-way dark outline the CML/TML 360
// labels already use (main.css:234) — added there for exactly this reason, so text
// stays legible on light colours and over arbitrary photo backgrounds.
// Exported so forgeService's near-duplicate createHotspotLabel dresses its labels
// from this one definition rather than drifting from it.
export const HOTSPOT_LABEL_CHIP_BG = 'rgba(0, 0, 0, 0.6)';
export const HOTSPOT_LABEL_TEXT_COLOR = '#ffffff';
export const HOTSPOT_LABEL_TEXT_SHADOW =
	'-1px -1px 0 rgba(0, 0, 0, 0.7), 1px -1px 0 rgba(0, 0, 0, 0.7), -1px 1px 0 rgba(0, 0, 0, 0.7), 1px 1px 0 rgba(0, 0, 0, 0.7)';
// Neutral grey the CML/TML 360 labels use as their own colour fallback (main.css:227).
const HOTSPOT_LABEL_FALLBACK_COLOR = '#616161';

/**
 * Normalizes a hotspot colour to `#rrggbb`, tolerating values passed with or without
 * the leading '#'. Module-scope rather than a method because createHotspotLabel is
 * handed to Pannellum unbound.
 * @param {string} color - Colour string from a tag model or TAG_COLOR_HEX
 * @returns {string} A valid CSS hex colour
 */
function toHexColor(color) {
	const hex = String(color ?? '').replace('#', '').trim();
	return hex ? `#${hex}` : HOTSPOT_LABEL_FALLBACK_COLOR;
}

/**
 * Manages hotspots in the panorama viewer
 * Handles adding, removing, and updating hotspots for tags, annotations, and other elements
 */
export class PanoramaHotspotManager {
	/**
	 * Creates a new instance of the PanoramaHotspotManager
	 * @param {Object} forgeService - Reference to the ForgeViewerService
	 */
	constructor(forgeService) {
		this.forgeService = forgeService;
		this.panoramaViewer = null;
		this.panoCustomSceneId = null;
		this.clustersIds = [];
		this.highlightId = null;
		this.tagHotspotSelectionGeneration = 0;
		this.tagHotspotSelection = null;
		this.tagHotspotSelectionOwner = null;
		this.tagHotspotSelectionReceipt = null;
		this.tagMarkerGeometryDecisions = new Map();
		this.tagHotspotSelectionActive = false;
		this.tagHotspotSelectionAwaitingViewerBinding = false;
		this.tagHotspotSelectionPreviousViewer = null;
		this.activeHotspotIds = []; // Array to track all active hotspot IDs
		this.cmlHotspotIds = []; // Separate tracking for CML hotspots (independent lifecycle)
		this._lastCmlInspection = null; // Store for re-render on image change
		this.tmlHotspotIds = []; // Separate tracking for TML hotspots (subordinated to CML, independent lifecycle)
		this._lastTmlInput = null; // Store for re-render on image change
		this.REFERENCE_POINT_ID = 'reference-point'; // Constant for reference point hotspot ID
	}

	/**
	 * Initializes the manager with the panorama viewer
	 * @param {Object} panoramaViewer - The panorama viewer instance
	 * @param {string} sceneId - The scene ID to use for hotspots
	 */
	initialize(panoramaViewer, sceneId) {
		const contextChanged = this.panoramaViewer !== panoramaViewer
			|| this.panoCustomSceneId !== sceneId;
		const selectionContextChanged = this.tagHotspotSelection
			&& (String(this.tagHotspotSelection.facilityId) !== String(store.state.assetId)
				|| String(this.tagHotspotSelection.scanId) !== String(panoramaViewer?.forgeImage?.id));
		const firstMatchingPendingBinding = this.tagHotspotSelectionAwaitingViewerBinding
			&& this.panoramaViewer === this.tagHotspotSelectionPreviousViewer
			&& !!panoramaViewer
			&& panoramaViewer !== this.tagHotspotSelectionPreviousViewer
			&& !selectionContextChanged;
		if ((contextChanged || selectionContextChanged)
			&& this.tagHotspotSelection
			&& !firstMatchingPendingBinding) {
			if (this.panoramaViewer) {
				this.panoramaViewer.removeHotSpot(
					this.tagHotspotSelection.tagId,
					this.panoCustomSceneId,
				);
			}
			this.activeHotspotIds = this.activeHotspotIds.filter(
				id => String(id) !== String(this.tagHotspotSelection.tagId),
			);
			this.resetTagHotspotSelection();
		}
		if (firstMatchingPendingBinding) {
			this.tagHotspotSelectionAwaitingViewerBinding = false;
			this.tagHotspotSelectionPreviousViewer = null;
		}
		this.panoramaViewer = panoramaViewer;
		this.panoCustomSceneId = sceneId;
	}

	get isGlobalSearchActive() {
		return store.state.isTabShown &&
			store.state.selectedTab === 0 &&
			!!store.getters['GET_GLOBAL_SEARCH_TEXT'] &&
			!!store.getters['GET_ISOLATED_IDS'].size
	}

	get isolatedTagsIds() {
		if (!this.isGlobalSearchActive) return new Set();
		return store.getters['GET_ISOLATED_IDS'];
	}

	getTagHotspotSelectionGeneration() {
		return this.tagHotspotSelectionGeneration;
	}

	resetTagHotspotSelection() {
		this.tagHotspotSelectionGeneration += 1;
		this.tagHotspotSelection = null;
		this.tagHotspotSelectionOwner = null;
		this.tagHotspotSelectionReceipt = null;
		this.tagHotspotSelectionActive = false;
		this.tagHotspotSelectionAwaitingViewerBinding = false;
		this.tagHotspotSelectionPreviousViewer = null;
	}

	beginTagHotspotSelection(seed, options = {}) {
		const previousSelectionId = this.tagHotspotSelection?.tagId;
		const preservedOwner = options.preserveOwner === true
			? this.tagHotspotSelectionOwner
			: null;
		this.tagHotspotSelectionGeneration += 1;
		this.tagHotspotSelection = createPanoramaTagSelectionScope({
			seed,
			generation: this.tagHotspotSelectionGeneration,
		});
		this.tagHotspotSelectionOwner = preservedOwner || Object.freeze({
			generation: this.tagHotspotSelectionGeneration,
			tagId: this.tagHotspotSelection.tagId,
			facilityId: this.tagHotspotSelection.facilityId,
			scanId: this.tagHotspotSelection.scanId,
		});
		this.tagHotspotSelectionReceipt = this.getTagMarkerGeometryDecision(
			this.tagHotspotSelection.tagId,
			this.tagHotspotSelection.facilityId,
		)?.receipt || null;
		this.tagHotspotSelectionActive = false;
		const previousViewer = options.previousViewer ?? null;
		this.tagHotspotSelectionAwaitingViewerBinding = options.awaitingViewerBinding === true
			&& this.panoramaViewer === previousViewer;
		this.tagHotspotSelectionPreviousViewer = this.tagHotspotSelectionAwaitingViewerBinding
			? previousViewer
			: null;

		if (this.panoramaViewer && previousSelectionId != null) {
			this.panoramaViewer.removeHotSpot(previousSelectionId, this.panoCustomSceneId);
			this.activeHotspotIds = this.activeHotspotIds.filter(
				id => String(id) !== String(previousSelectionId),
			);
		}
		if (this.panoramaViewer && this.highlightId) {
			this.panoramaViewer.removeHotSpot(this.highlightId, this.panoCustomSceneId);
		}

		return this.tagHotspotSelection;
	}

	selectTagHotspot(selection, currentGeneration, receipt = null) {
		if (!selection || selection.generation !== currentGeneration) return false;
		if (currentGeneration !== this.tagHotspotSelectionGeneration) return false;
		if (this.tagHotspotSelection?.generation !== currentGeneration) return false;
		if (String(selection.tagId) !== String(this.tagHotspotSelection.tagId)) return false;

		this.tagHotspotSelection = selection;
		if (receipt != null) this.recordTagMarkerGeometryDecision(receipt);
		const decision = this.getTagMarkerGeometryDecision(selection.tagId, selection.facilityId);
		if (decision) this.tagHotspotSelectionReceipt = decision.receipt;
		else if (receipt != null) this.tagHotspotSelectionReceipt = receipt;
		if (this.receiptInvalidatesSelectionGeometry(this.tagHotspotSelectionReceipt || {})) {
			this.tagHotspotSelectionActive = false;
			this.updateAllHotspots();
			return false;
		}
		this.tagHotspotSelectionActive = true;
		this.updateAllHotspots();
		return true;
	}

	clearTagHotspotSelection(currentGeneration) {
		if (currentGeneration !== this.tagHotspotSelectionGeneration) return false;
		if (!this.tagHotspotSelection) return false;

		this.resetTagHotspotSelection();
		this.updateAllHotspots();
		return true;
	}

	clearOwnedTagHotspotSelection(ownerScope) {
		const owner = this.tagHotspotSelectionOwner;
		const selection = this.tagHotspotSelection;
		if (!ownerScope || !owner || !selection) return false;
		if (ownerScope.generation !== owner.generation) return false;
		if (String(ownerScope.tagId) !== String(owner.tagId)) return false;
		if (String(ownerScope.facilityId) !== String(owner.facilityId)) return false;
		if (String(ownerScope.scanId) !== String(owner.scanId)) return false;
		return this.clearTagHotspotSelection(this.tagHotspotSelectionGeneration);
	}

	ownsTagHotspotSelection(ownerScope) {
		const owner = this.tagHotspotSelectionOwner;
		return !!(ownerScope && owner && this.tagHotspotSelection)
			&& ownerScope.generation === owner.generation
			&& String(ownerScope.tagId) === String(owner.tagId)
			&& String(ownerScope.facilityId) === String(owner.facilityId)
			&& String(ownerScope.scanId) === String(owner.scanId);
	}

	invalidateTagHotspotSelection(receipt, currentGeneration, options = {}) {
		const render = options.render !== false;
		this.recordTagMarkerGeometryDecision(receipt);
		const selection = this.tagHotspotSelection;
		const awaitingViewerBinding = this.tagHotspotSelectionAwaitingViewerBinding;
		const previousViewer = this.tagHotspotSelectionPreviousViewer;
		if (!selection || currentGeneration !== this.tagHotspotSelectionGeneration) return false;
		if (!receipt
			|| String(receipt.tagId) !== String(selection.tagId)
			|| String(receipt.facilityId) !== String(selection.facilityId)) return false;

		if (this.receiptRepositionsSelection(receipt)) {
			this.beginTagHotspotSelection(
				{ ...selection, point: receipt.authoritativePoint },
				{ awaitingViewerBinding, previousViewer, preserveOwner: true },
			);
			this.tagHotspotSelectionActive = true;
			if (render) this.updateAllHotspots();
			return true;
		}
		if (this.receiptInvalidatesSelectionGeometry(receipt)) {
			this.beginTagHotspotSelection(selection, {
				awaitingViewerBinding,
				previousViewer,
				preserveOwner: true,
			});
			this.tagHotspotSelectionReceipt = receipt;
			this.tagHotspotSelectionActive = false;
			if (render) this.updateAllHotspots();
			return true;
		}

		this.tagHotspotSelectionReceipt = this.getTagMarkerGeometryDecision(
			receipt.tagId,
			receipt.facilityId,
		)?.receipt || receipt;
		if (render) this.updateAllHotspots();
		return true;
	}

	receiptRepositionsSelection(receipt) {
		return receipt.outcome === TAG_MARKER_OUTCOME.CONFIRMED_SUCCESS
			&& receipt.confirmedAbsentInScope !== true
			&& Object.prototype.hasOwnProperty.call(receipt, 'authoritativePoint')
			&& receipt.authoritativePoint != null;
	}

	receiptInvalidatesSelectionGeometry(receipt) {
		if (receipt.outcome === TAG_MARKER_OUTCOME.WRITE_SUCCEEDED_REFRESH_FAILED) return true;
		return receipt.outcome === TAG_MARKER_OUTCOME.CONFIRMED_SUCCESS
			&& (receipt.confirmedAbsentInScope === true
				|| (Object.prototype.hasOwnProperty.call(receipt, 'authoritativePoint')
					&& receipt.authoritativePoint === null));
	}

	tagMarkerGeometryDecisionKey(tagId, facilityId) {
		if (tagId == null || facilityId == null) return null;
		return JSON.stringify([String(facilityId), String(tagId)]);
	}

	getTagMarkerGeometryDecision(tagId, facilityId) {
		const key = this.tagMarkerGeometryDecisionKey(tagId, facilityId);
		return key == null ? null : this.tagMarkerGeometryDecisions.get(key) || null;
	}

	recordTagMarkerGeometryDecision(receipt) {
		if (!receipt || (!this.receiptRepositionsSelection(receipt)
			&& !this.receiptInvalidatesSelectionGeometry(receipt))) return false;
		const key = this.tagMarkerGeometryDecisionKey(receipt.tagId, receipt.facilityId);
		if (key == null) return false;
		this.tagMarkerGeometryDecisions.set(key, Object.freeze({ receipt }));
		return true;
	}

	recordAuthoritativeTagGeometry(receipt, expectedDecision, options = {}) {
		if (!receipt) return false;
		if (this.getTagMarkerGeometryDecision(receipt.tagId, receipt.facilityId) !== expectedDecision) {
			return false;
		}
		if (!this.recordTagMarkerGeometryDecision(receipt)) return false;
		const selection = this.tagHotspotSelection;
		if (selection
			&& String(selection.tagId) === String(receipt.tagId)
			&& String(selection.facilityId) === String(receipt.facilityId)) {
			this.tagHotspotSelectionReceipt = receipt;
			this.tagHotspotSelectionActive = !this.receiptInvalidatesSelectionGeometry(receipt);
			if (options.render !== false) this.updateAllHotspots();
		}
		return true;
	}

	selectionMatchesCurrentContext() {
		const selection = this.tagHotspotSelection;
		const imageId = this.panoramaViewer?.forgeImage?.id;
		return selection?.generation === this.tagHotspotSelectionGeneration
			&& String(selection.facilityId) === String(store.state.assetId)
			&& String(selection.scanId) === String(imageId);
	}

	filterOrdinaryTagMarkers(tags) {
		const suppressSelection = this.selectionMatchesCurrentContext()
			&& (this.tagHotspotSelectionActive
				|| this.receiptInvalidatesSelectionGeometry(this.tagHotspotSelectionReceipt || {}));
		const selectedId = this.tagHotspotSelection?.tagId;
		return (tags || []).filter(tag => tag?.isDeleted !== true
			&& !this.receiptInvalidatesSelectionGeometry(
				this.getTagMarkerGeometryDecision(tag?.id, store.state.assetId)?.receipt || {},
			)
			&& (!suppressSelection || String(tag.id) !== String(selectedId)));
	}

	getSelectedTagPoint() {
		const selection = this.tagHotspotSelection;
		const receipt = this.tagHotspotSelectionReceipt;
		const receiptMatches = receipt
			&& String(receipt.tagId) === String(selection?.tagId)
			&& String(receipt.facilityId) === String(selection?.facilityId);
		if (receiptMatches
			&& receipt.outcome === TAG_MARKER_OUTCOME.CONFIRMED_SUCCESS
			&& Object.prototype.hasOwnProperty.call(receipt, 'authoritativePoint')) {
			return receipt.authoritativePoint;
		}
		return selection?.point;
	}

	renderSelectedTagHotspot(currentGeneration) {
		const selection = this.tagHotspotSelection;
		if (!this.tagHotspotSelectionActive || currentGeneration !== this.tagHotspotSelectionGeneration) return false;
		if (!canRenderSelectedPointMarker({
			selection,
			currentGeneration,
			currentFacilityId: store.state.assetId,
			currentScanId: this.panoramaViewer?.forgeImage?.id,
			receipt: this.tagHotspotSelectionReceipt,
		})) return false;

		const selectedPoint = this.getSelectedTagPoint();
		const selectedTag = {
			...selection,
			id: selection.tagId,
			point: new THREE.Vector3(selectedPoint.x, selectedPoint.y, selectedPoint.z),
		};
		this.addTagHotspotByFamily(selectedTag, { selected: true });
		return true;
	}

	addTagHotspotByFamily(tag, options = {}) {
		const markerFamily = String(tag?.markerFamily || tag?.type || '').toLowerCase();
		if (markerFamily === 'equipment') return this.addEquipmentHotSpot(tag, options);
		if (markerFamily === 'pipeline') return this.addPipelineHotSpot(tag, options);
		if (markerFamily === 'structural') return this.addStructuralHotSpot(tag, options);
		if (markerFamily === 'inspection') return this.addInspectionHotSpot(tag, options);
		if (markerFamily === 'anomaly') return this.addAnomalyHotSpot(tag, this.panoramaViewer.forgeImage, options);
		if (markerFamily === 'ava-inspect') return this.addAvaInspectHotSpot(tag, options);
		if (markerFamily === 'scaffold') return this.addScaffoldHotSpot(tag, options);

		return this.addGenericSelectedTagHotspot(tag, options);
	}

	addGenericSelectedTagHotspot(tag, options) {
		const isAnnotation = tag.markerFamily === 'annotation';
		const labelClass = isAnnotation ? 'annotation-3d-pano' : 'tag-3d-pano';
		return this.addGenericHotspot(
			tag,
			this.panoramaViewer.forgeImage,
			this.selectedClassName(labelClass, options),
			isAnnotation ? 'annotation-360-hotspot' : 'tag-360-hotspot',
			'general',
			this.createAuthoritativeTagHotspotClickHandler(tag),
		);
	}

	createAuthoritativeTagHotspotClickHandler(tag) {
		const generation = tag.generation;
		const facilityId = tag.facilityId;
		return event => {
			if (!this.isCurrentTagHotspotSelection(tag.id, facilityId, generation)) {
				return Promise.resolve();
			}
			const offset = this.forgeService.panoEventToContainerOffset(event);
			return this.readAuthoritativeAnnotation(tag.id, facilityId).then(authoritativeTag => {
				if (!this.isCurrentTagHotspotSelection(tag.id, facilityId, generation)) return;
				this.forgeService.onClickOn3dIcon(authoritativeTag, offset, '360');
			}).catch(err => {
				if (!this.isCurrentTagHotspotSelection(tag.id, facilityId, generation)) return;
				debugError(`[360] Failed to hydrate selected annotation ${tag.id}`, err);
			});
		};
	}

	readAuthoritativeAnnotation(tagId, facilityId) {
		const loadedAnnotation = (store.state.annotations || [])
			.find(annotation => String(annotation.id) === String(tagId));
		if (loadedAnnotation) return Promise.resolve(loadedAnnotation);
		return store.dispatch('GET_BASE_TAG_BY_ID', {
			id: tagId,
			assetId: facilityId,
			expectedKind: null,
		});
	}

	isCurrentTagHotspotSelection(tagId, facilityId, generation) {
		const selection = this.tagHotspotSelection;
		return this.tagHotspotSelectionActive
			&& generation === this.tagHotspotSelectionGeneration
			&& selection?.generation === generation
			&& String(selection.tagId) === String(tagId)
			&& String(selection.facilityId) === String(facilityId)
			&& this.selectionMatchesCurrentContext();
	}

	selectedClassName(className, options = {}) {
		return options.selected ? `${className} selected` : className;
	}

	/**
	 * Updates all hotspots in the panorama viewer
	 * Renders various hotspots for tags, annotations, and other elements in the 360 degree panorama view
	 */
	updateAllHotspots() {
		if (!this.panoramaViewer) return;

		// Clear previous hotspots
		this.clearAllHotspots();

		this.updateAnnotationHotspots();
		this.updateAnomaliesHotSpots();
		this.updatePipelinesHotSpots();
		this.updateStructuralHotSpots();
		this.updateEquipmentHotspots();
		this.updateInspectionsHotspots();
		this.updateAvaInspectHotspots();
		this.updateScaffoldHotspots();
		this.renderSelectedTagHotspot(this.tagHotspotSelectionGeneration);

		// Re-render CML hotspots with updated yaw/pitch for the new camera position
		if (this._lastCmlInspection) {
			this.showCmlHotspots(this._lastCmlInspection);
		}

		// Re-render TML hotspots too — same lifecycle as CML (both anchor to the
		// equipment master tag and must re-project for the new camera position).
		if (this._lastTmlInput) {
			this.showTmlHotspots(this._lastTmlInput);
		}
	}

	/**
	 * Clears all hotspots from the panorama viewer
	 */
	clearAllHotspots(options = {}) {
		const clearTagSelection = options.clearTagSelection === true;
		const viewer = this.panoramaViewer;
		// Clear all tracked hotspots at once using the activeHotspotIds array
		this.activeHotspotIds.forEach(id => {
			viewer?.removeHotSpot(id, this.panoCustomSceneId);
		});

		// Reset the tracking array
		this.activeHotspotIds = [];

		// Clear clusters separately as they're managed differently
		this.clustersIds.forEach(id => {
			viewer?.removeHotSpot(id, this.panoCustomSceneId);
		});

		this.clustersIds = [];

		// Remove highlight
		if (this.highlightId) {
			viewer?.removeHotSpot(this.highlightId, this.panoCustomSceneId);
		}

		if (clearTagSelection) this.resetTagHotspotSelection();
	}

	/**
	 * Updates AVA Inspect hotspots in the panorama viewer
	 */
	updateAvaInspectHotspots() {
		if (!store.state.userSettings.displaySettings.showAvaInspect) {
			return;
		}

		const image = this.panoramaViewer.forgeImage;
		if (!image) return;

		const position = image.forgeCameraPosition;
		if (!position) return;

		const visRange = this.getVisibilityRange();

		let avaInspects = this.filterOrdinaryTagMarkers(store.getters.GET_AVA_INSPECT_TAGS || []);

		if (this.isGlobalSearchActive) {
			avaInspects = avaInspects.filter((o) => this.isolatedTagsIds.has(o.id));
		}

		avaInspects = this.filterByVisibilityRange(avaInspects, image.forgeCameraPosition, visRange);

		let { items, clusters } = store.state.forgeService.getTagsClustersAndItems(avaInspects, position, visRange);

		clusters.forEach((o, index) => {
			var box = new THREE.Box3().setFromPoints(o.map((o) => o.point));
			var center = box.getCenter();
			var direction = new THREE.Vector3();
			direction.subVectors(center, position).normalize();
			var info = this.forgeService.getNewYawAndPitch(image, direction);
			var id = `auto-tag-cluster-${index}`;
			this.clustersIds.push(id);
			let hotspotImg = {
				id: id,
				pitch: info['pitch'],
				yaw: info['yaw'],
				cssClass: 'custom-hotspot',
				createTooltipFunc: this.clusterHotSpot,
				createTooltipArgs: { items: o, position: center, image: image, type: 'ava-inspect' },
				clickHandlerFunc: (event, args) => {
					store.commit('setTooltipObject', null);
					this.clustersIds = this.clustersIds.filter((i) => i !== args.id);
					this.panoramaViewer.removeHotSpot(args.id, this.panoCustomSceneId);
					args.items.forEach((o) => {
						this.addAvaInspectHotSpot(o);
					});
				},
				clickHandlerArgs: { id: id, items: o, index },
			};

			this.trackAndAddHotspot(hotspotImg, this.panoCustomSceneId);
		});

		items.forEach((o) => {
			this.addAvaInspectHotSpot(o);
		});
	}

	/**
	 * Adds an AVA Inspect hotspot to the panorama viewer
	 * @param {Object} o - The AVA Inspect object
	 */
	addAvaInspectHotSpot(o, options = {}) {
		const image = this.panoramaViewer.forgeImage;
		const position = image.forgeCameraPosition;

		let direction = new THREE.Vector3();
		direction.subVectors(o.point, position).normalize();

		let info = this.forgeService.getNewYawAndPitch(image, direction);

		let hotspotIcon = {
			id: o.id,
			pitch: info['pitch'],
			yaw: info['yaw'],
			cssClass: 'custom-hotspot',
			createTooltipFunc: this.createHotspotLabel,
			createTooltipArgs: {
				title: o.name,
				category: o.category,
				className: this.selectedClassName('tag-3d-pano', options),
				imgClass: 'tag-360-hotspot',
				imageName: 'ava-inspect',
			},
			clickHandlerFunc: function (event) {
				store.dispatch('FETCH_AVA_INSPECT_TAG_BY_ID', o.id).then((tag) => {
					store.state.forgeService.onClickOn3dIcon(tag, store.state.forgeService.panoEventToContainerOffset(event), '360');
				});
			},
		};

		this.trackAndAddHotspot(hotspotIcon, this.panoCustomSceneId);
	}

	getVisibilityRange() {
		// tag.point and forgeCameraPosition are both in Forge viewer-space (metres),
		// so compare directly against the user's metre setting — no unit conversion needed.
		// Cap at 15m for the 360 pano viewer — beyond 15m is not useful in panorama context.
		const range = store.state.userSettings.displaySettings.getVisibilityRange();
		return Math.min(range, 15);
	}

	/**
	 * Updates annotation hotspots in the panorama viewer
	 */
	updateAnnotationHotspots() {
		if (!store.state.userSettings.displaySettings.showAnnotations) {
			return;
		}

		const visRange = this.getVisibilityRange();

		const image = this.panoramaViewer.forgeImage;

		if (!image) return;

		let annotations = this.filterOrdinaryTagMarkers(store.state.annotations).filter((o) => !o.level2D);

		if (this.isGlobalSearchActive) {
			annotations = annotations.filter((o) => this.isolatedTagsIds.has(o.id));
		}

		// Filter annotations by visibility range
		annotations = this.filterByVisibilityRange(annotations, image.forgeCameraPosition, visRange);

		// Add hotspots for filtered annotations
		annotations.forEach(annotation => {
			this.addGenericHotspot(
				annotation,
				image,
				'annotation-3d-pano',
				'annotation-360-hotspot',
				'general',
				(event) => this.forgeService.onClickOn3dIcon(annotation, store.state.forgeService.panoEventToContainerOffset(event), '360')
			);
		});
	}

	/**
	 * Updates auto equipment hotspots in the panorama viewer
	 */
	updateEquipmentHotspots() {
		if (!store.state.userSettings.displaySettings.showAutoEquipment) {
			return;
		}

		const image = this.panoramaViewer.forgeImage;

		if (!image) return;

		const visRange = this.getVisibilityRange();

		const position = image.forgeCameraPosition;

		const autoTags = this.filterOrdinaryTagMarkers(
			store.getters['GET_ALL_EXISTING_AUTOMATIC_TAGS'].concat(store.state.tags),
		);

		// Filter tags by visibility range
		let equipments = this.filterByVisibilityRange(autoTags, image.forgeCameraPosition, visRange);

		if (this.isGlobalSearchActive) {
			equipments = equipments.filter((o) => this.isolatedTagsIds.has(o.id));
		}

		let { items, clusters } = store.state.forgeService.getTagsClustersAndItems(equipments, position, visRange);

		clusters.forEach((o, index) => {
			var box = new THREE.Box3().setFromPoints(o.map((o) => o.point));
			var center = box.getCenter();
			var direction = new THREE.Vector3();
			direction.subVectors(center, position).normalize();
			var info = this.forgeService.getNewYawAndPitch(image, direction);
			var id = `auto-tag-cluster-${index}`;
			this.clustersIds.push(id);
			let hotspotImg = {
				id: id,
				pitch: info['pitch'],
				yaw: info['yaw'],
				cssClass: 'custom-hotspot',
				createTooltipFunc: this.clusterHotSpot,
				createTooltipArgs: { items: o, position: center, image: image, type: 'equipment' },
				clickHandlerFunc: (event, args) => {
					store.commit('setTooltipObject', null);
					this.clustersIds = this.clustersIds.filter((i) => i !== args.id);
					this.panoramaViewer.removeHotSpot(args.id, this.panoCustomSceneId);
					args.items.forEach((o) => {
						this.addEquipmentHotSpot(o);
					});
				},
				clickHandlerArgs: { id: id, items: o, index },
			};

			this.trackAndAddHotspot(hotspotImg, this.panoCustomSceneId);
		});

		// Add hotspots for filtered tags
		items.forEach(tag => {
			this.addEquipmentHotSpot(tag);
		});
	}

	addEquipmentHotSpot(o, options = {}) {
		const image = this.panoramaViewer.forgeImage;
		if (!image) return;

		const position = image.forgeCameraPosition;
		if (!position) return;

		const direction = new THREE.Vector3().subVectors(o.point, position).normalize();
		const info = this.forgeService.getNewYawAndPitch(image, direction);

		const isPositioned = !!o.positionImage360Id;
		const hotspotIcon = {
			id: o.id,
			pitch: info['pitch'],
			yaw: info['yaw'],
			cssClass: isPositioned ? 'custom-hotspot positioned-tag-hotspot' : 'custom-hotspot',
			createTooltipFunc: this.createHotspotLabel,
			createTooltipArgs: {
				title: o.name,
				category: isPositioned ? 'Positioned' : o.category,
				className: this.selectedClassName(
					isPositioned ? 'tag-3d-pano positioned-tag-label' : 'tag-3d-pano',
					options,
				),
				imgClass: 'tag-360-hotspot',
				imageName: 'equipment',
			},
			clickHandlerFunc: (event) => {
				store.dispatch('FETCH_EQUIPMENT_TAG_BY_ID', o.id).then((tag) => {
					this.forgeService.onClickOn3dIcon(
						tag,
						store.state.forgeService.panoEventToContainerOffset(event),
						'360',
					);
				});
			},
		};

		this.trackAndAddHotspot(hotspotIcon, this.panoCustomSceneId);
	}

	/**
	 * Updates scaffold-family hotspots (Scaffold / Permit / Container) in the panorama viewer.
	 * Each of the three types honours its own display toggle, mirroring the 3D
	 * point-cloud path (ScaffoldHandler.shouldProcess, scaffoldHandler.js:16-19).
	 */
	updateScaffoldHotspots() {
		const displaySettings = store.state.userSettings.displaySettings;

		if (!displaySettings.showScaffolds && !displaySettings.showPermits && !displaySettings.showDeckManagment) {
			return;
		}

		const image = this.panoramaViewer.forgeImage;
		if (!image) return;

		const position = image.forgeCameraPosition;
		if (!position) return;

		const visRange = this.getVisibilityRange();

		// isScaffoldPlaced first: a register-created or cleared tag sits at the origin, and a
		// hotspot there would show up in any 360 scan within the visibility range of it.
		let scaffolds = this.filterOrdinaryTagMarkers(store.state.scaffolds || [])
			.filter((o) => isScaffoldPlaced(o) && this.isScaffoldTypeVisible(o, displaySettings));

		if (this.isGlobalSearchActive) {
			scaffolds = scaffolds.filter((o) => this.isolatedTagsIds.has(o.id));
		}

		scaffolds = this.filterByVisibilityRange(scaffolds, position, visRange);

		debugLog(`[360-Scaffold] ${scaffolds.length} scaffold-family hotspot(s) within ${visRange}m`);

		// No clustering here: scaffold-family counts are low and the 15m visibility
		// cap already bounds how many can share the view.
		scaffolds.forEach((o) => {
			this.addScaffoldHotSpot(o);
		});
	}

	/**
	 * Maps a scaffold-family tag to its own display toggle
	 * @param {Object} o - The scaffold-family tag
	 * @param {Object} displaySettings - store.state.userSettings.displaySettings
	 * @returns {boolean} Whether this tag's type is currently shown
	 */
	isScaffoldTypeVisible(o, displaySettings) {
		if (typeof o.isScaffold !== 'function') return false;

		if (o.isScaffold()) return !!displaySettings.showScaffolds;
		if (o.isPermit()) return !!displaySettings.showPermits;
		if (o.isContainer()) return !!displaySettings.showDeckManagment;

		return false;
	}

	/**
	 * Resolves the colour for a scaffold-family hotspot — state green/orange/red,
	 * the same source the 3D dot uses (ForgeScaffold.getForgeColor, which itself
	 * falls back to the per-type TAG_COLOR_HEX when the state is unrecognised,
	 * forgeModels.js:2302-2308). The final fallback covers plain objects reaching
	 * create360HighlightHotspot by `type` string alone.
	 * @param {Object} o - The scaffold-family tag
	 * @returns {string} Hex colour string
	 */
	getScaffoldColor(o) {
		const color = typeof o.getForgeColor === 'function' ? o.getForgeColor() : null;
		if (color) return color;

		const stateIndex = (this.forgeService.scaffoldStates || [])
			.findIndex(scaffoldState => scaffoldState.state == o.state);
		return ['#92D050', '#ffa500', '#ff0000'][stateIndex] || TAG_COLOR_HEX.Scaffold;
	}

	/**
	 * Adds a scaffold-family hotspot to the panorama viewer.
	 * Renders through createHotspotLabelByColor because no scaffold/permit/container
	 * PNG exists in @/images/tags-icons — createHotspotLabel require()s
	 * `${imageName}-hover.png` and would throw.
	 * @param {Object} o - The scaffold-family tag
	 */
	addScaffoldHotSpot(o, options = {}) {
		const image = this.panoramaViewer.forgeImage;
		if (!image) return;

		const position = image.forgeCameraPosition;
		if (!position) return;

		// Anchored on the stored position (box centre) — the point the user picked —
		// with no height offset, so the 360 icon agrees with the 3D marker, the box
		// mesh and the crop box (ScaffoldHandler.getPointForScaffold).
		const direction = new THREE.Vector3().subVectors(o.point, position).normalize();
		const info = this.forgeService.getNewYawAndPitch(image, direction);

		const isPositioned = !!o.positionImage360Id;
		const hotspotIcon = {
			id: o.id,
			pitch: info['pitch'],
			yaw: info['yaw'],
			cssClass: isPositioned ? 'custom-hotspot positioned-tag-hotspot' : 'custom-hotspot',
			createTooltipFunc: this.createHotspotLabelByColor.bind(this),
			createTooltipArgs: {
				title: o.name,
				category: isPositioned ? 'Positioned' : o.category,
				className: this.selectedClassName(
					isPositioned ? 'tag-3d-pano positioned-tag-label' : 'tag-3d-pano',
					options,
				),
				imgClass: 'tag-360-hotspot',
				imageName: o.type,
				color: this.getScaffoldColor(o),
			},
			clickHandlerFunc: (event) => {
				store.dispatch('FETCH_SCAFFOLD_TAG_BY_ID', o.id).then((tag) => {
					this.forgeService.onClickOn3dIcon(
						tag,
						store.state.forgeService.panoEventToContainerOffset(event),
						'360',
					);
				});
			},
		};

		try {
			this.trackAndAddHotspot(hotspotIcon, this.panoCustomSceneId);
		} catch (err) {
			debugError(`[360-Scaffold] failed to add hotspot for ${o.name}`, err);
		}
	}

	/**
	 * Creates a highlight hotspot in the panorama viewer
	 * @param {Object} o - The object to highlight
	 */
	create360HighlightHotspot(tag, options = {}) {
		const image = this.panoramaViewer?.forgeImage;
		if (!image || !tag) return false;

		const seed = createPanoramaTagSelectionSeed({
			tag,
			facilityId: options.facilityId ?? store.state.assetId,
			 scanId: options.scanId ?? image.id,
		});
		if (!seed.point) return false;
		const selection = this.beginTagHotspotSelection(seed);
		return this.selectTagHotspot(
			selection,
			this.getTagHotspotSelectionGeneration(),
			options.receipt ?? null,
		);
	}

	updateAnomaliesHotSpots() {
		if (!store.state.userSettings.displaySettings.showAnomalies) {
			return;
		}

		let visRange = this.getVisibilityRange();

		let anomalies = this.filterOrdinaryTagMarkers(store.getters.GET_ALL_EXISTING_ANOMALIES_TAGS);

		const image = this.panoramaViewer.forgeImage;
		let position = image?.forgeCameraPosition;

		if (!image || !position) return;

		if (this.isGlobalSearchActive) {
			anomalies = anomalies.filter((o) => this.isolatedTagsIds.has(o.id));
		}

		anomalies.forEach((o) => {
			if (!o.point || o.point.distanceTo(position) > visRange) return;
			this.addAnomalyHotSpot(o, image);
		});
	}

	/**
	 * Adds an anomaly hotspot to the panorama viewer
	 * @param {Object} o - The anomaly object
	 * @param {THREE.Vector3} position - The position of the hotspot
	 * @param {Object} image - The image object
	 */
	addAnomalyHotSpot(o, image, options = {}) {
		let position = image.forgeCameraPosition;

		var direction = new THREE.Vector3();

		direction.subVectors(o.point, position).normalize();

		var info = this.forgeService.getNewYawAndPitch(image, direction);

		let hotspotConfig = {
			id: o.id,
			pitch: info['pitch'],
			yaw: info['yaw'],
			cssClass: 'custom-hotspot',
			createTooltipFunc: this.createHotspotLabelByColor.bind(this),
			createTooltipArgs: {
				title: o.name,
				category: o.category,
				className: this.selectedClassName('tag-3d-pano', options),
				imgClass: 'tag-360-hotspot',
				imageName: 'anomaly',
				color: ForgeAnomaly.getConditionColor(o.condition)
			},
			clickHandlerFunc: function (event) {
				store.dispatch('FETCH_ANOMALY_TAG_BY_ID', o.id).then((tag) => {
					store.state.forgeService.onClickOn3dIcon(tag, store.state.forgeService.panoEventToContainerOffset(event), '360');
				});
			},
			clickHandlerArgs: 'test',
		};

		try {
			this.trackAndAddHotspot(hotspotConfig, this.panoCustomSceneId);
		} catch (err) {
			debugLog(err);
		}
	}

	/**
	 * Creates an HTML label for a hotspot based on color.
	 * The tag colour drives the icon only — see HOTSPOT_LABEL_CHIP_BG for why the
	 * chip stays neutral.
	 * @param {HTMLElement} hotSpotDiv - The hotspot div element
	 * @param {Object} args - Arguments for the hotspot label
	 */
	createHotspotLabelByColor(hotSpotDiv, args) {
		var fullColor = toHexColor(args.color);

		var label = document.createElement('div');
		label.className = args.className;
		label.style.backgroundColor = HOTSPOT_LABEL_CHIP_BG;

		var icon = document.createElement('img');
		icon.className = 'tag-360 ' + args.imgClass;

		// Generate dynamic icon using shader-based logic instead of loading image files
		icon.src = getCachedDynamicIcon(fullColor, 50, true); // 50px size, normal state

		// Create hover version for potential hover effects
		// const hoverIconSrc = getCachedDynamicIcon(fullColor, 50, true);
		// icon.dataset.hoverSrc = hoverIconSrc;
		icon.dataset.normalSrc = icon.src;

		// Add hover event listeners for dynamic icon switching
		// this.addDynamicIconHoverEffects(icon);

		label.appendChild(icon);

		var textDiv = document.createElement('div');
		textDiv.innerText = args.title;
		textDiv.className = 'tag-title';
		textDiv.style.color = HOTSPOT_LABEL_TEXT_COLOR;
		textDiv.style.textShadow = HOTSPOT_LABEL_TEXT_SHADOW;
		label.appendChild(textDiv);

		if (args.category) store.state.forgeService.addCategoryColorToLabel(args.category, label);
		hotSpotDiv.appendChild(label);
	}

	/**
	 * Adds hover effects to dynamically generated icons
	 * @param {HTMLImageElement} iconElement - The icon element to add hover effects to
	 */
	addDynamicIconHoverEffects(iconElement) {
		if (!iconElement.dataset.hoverSrc || !iconElement.dataset.normalSrc) return;

		iconElement.addEventListener('mouseenter', () => {
			iconElement.src = iconElement.dataset.hoverSrc;
		});

		iconElement.addEventListener('mouseleave', () => {
			iconElement.src = iconElement.dataset.normalSrc;
		});
	}

	/**
	 * Updates manual pipelines hotspots in the panorama viewer
	 * @param {Array} manualPipelines - Array of manual pipelines
	 * @param {number} visRange - Visibility range
	 */
	updatePipelinesHotSpots() {
		if (!store.state.userSettings.displaySettings.showManualPipelines) {
			return;
		}

		const image = this.panoramaViewer.forgeImage;

		const position = image?.forgeCameraPosition;

		if (!image || !position) return;

		let visRange = this.getVisibilityRange();

		let tags = this.filterOrdinaryTagMarkers(
			store.getters.GET_ALL_EXISTING_PIPELINES_TAGS.concat(store.getters.GET_MANUAL_PIPELINES_TAGS),
		).filter((o) => !o.level2D);

		if (this.isGlobalSearchActive) {
			tags = tags.filter((o) => this.isolatedTagsIds.has(o.id));
		}

		let { items, clusters } = store.state.forgeService.getTagsClustersAndItems(tags, position, visRange);

		clusters.forEach((o, index) => {
			var box = new THREE.Box3().setFromPoints(o.map((o) => o.point));
			var center = box.getCenter();
			var direction = new THREE.Vector3();
			direction.subVectors(center, position).normalize();
			var info = this.forgeService.getNewYawAndPitch(image, direction);
			var id = `auto-tag-cluster-${index}`;
			this.clustersIds.push(id);
			let hotspotImg = {
				id: id,
				pitch: info['pitch'],
				yaw: info['yaw'],
				cssClass: 'custom-hotspot',
				createTooltipFunc: this.clusterHotSpot,
				createTooltipArgs: { items: o, position: center, image: image, type: 'pipeline' },
				clickHandlerFunc: (event, args) => {
					store.commit('setTooltipObject', null);
					this.clustersIds = this.clustersIds.filter((i) => i !== args.id);
					this.panoramaViewer.removeHotSpot(args.id, this.panoCustomSceneId);
					args.items.forEach((o) => {
						this.addPipelineHotSpot(o);
					});
				},
				clickHandlerArgs: { id: id, items: o, index },
			};

			this.trackAndAddHotspot(hotspotImg, this.panoCustomSceneId);
		});

		items.forEach(tag => {
			this.addPipelineHotSpot(tag);
		});
	}

	/**
	 * Adds a pipeline hotspot to the panorama viewer
	 * @param {Object} o - The pipeline object
	 * @param {THREE.Vector3} position - The position of the hotspot
	 * @param {Object} image - The image object
	 * @param {Function} hotspot - The hotspot tooltip function
	 */
	addPipelineHotSpot(o, options = {}) {
		const image = this.panoramaViewer.forgeImage;
		const position = image.forgeCameraPosition;

		let direction = new THREE.Vector3();
		direction.subVectors(o.point, position).normalize();

		var info = this.forgeService.getNewYawAndPitch(image, direction);
		const isPositioned = !!o.positionImage360Id;
		let hotspotIcon = {
			id: o.id,
			pitch: info['pitch'],
			yaw: info['yaw'],
			cssClass: isPositioned ? 'custom-hotspot positioned-tag-hotspot' : 'custom-hotspot',
			createTooltipFunc: this.createHotspotLabel,
			createTooltipArgs: {
				title: o.name,
				category: isPositioned ? 'Positioned' : o.category,
				className: this.selectedClassName(
					isPositioned ? 'tag-3d-pano positioned-tag-label' : 'tag-3d-pano',
					options,
				),
				imgClass: 'tag-360-hotspot',
				imageName: 'pipeline',
			},
			clickHandlerFunc: function (event) {
				store.dispatch('FETCH_PIPELINE_TAG_BY_ID', o.id).then((tag) => {
					store.state.forgeService.onClickOn3dIcon(
						tag,
						store.state.forgeService.panoEventToContainerOffset(event),
						'360',
					);
				});
			},
			clickHandlerArgs: 'test',
		};

		this.trackAndAddHotspot(hotspotIcon, this.panoCustomSceneId);
	}

	/**
	 * Updates manual structural hotspots in the panorama viewer
	 */
	updateStructuralHotSpots() {
		if (!store.state.userSettings.displaySettings.showManualStructural) {
			return;
		}

		let image = this.panoramaViewer?.forgeImage;

		let position = image?.forgeCameraPosition;

		if (!image || !position) return;

		let visRange = this.getVisibilityRange();

		let structural = this.filterOrdinaryTagMarkers(
			store.getters.GET_ALL_EXISTING_STRUCTURAL_TAGS.concat(store.getters.GET_MANUAL_STRUCTURAL_TAGS),
		).filter((o) => !o.level2D);

		if (this.isGlobalSearchActive) {
			structural = structural.filter((o) => this.isolatedTagsIds.has(o.id));
		}

		let { items, clusters } = store.state.forgeService.getTagsClustersAndItems(structural, position, visRange);

		clusters.forEach((o, index) => {
			var box = new THREE.Box3().setFromPoints(o.map((o) => o.point));
			var center = box.getCenter();
			var direction = new THREE.Vector3();
			direction.subVectors(center, position).normalize();
			var info = this.forgeService.getNewYawAndPitch(image, direction);
			var id = `auto-tag-cluster-${index}`;
			this.clustersIds.push(id);
			let hotspotImg = {
				id: id,
				pitch: info['pitch'],
				yaw: info['yaw'],
				cssClass: 'custom-hotspot',
				createTooltipFunc: this.clusterHotSpot,
				createTooltipArgs: { items: o, position: center, image: image, type: 'structural' },
				clickHandlerFunc: (event, args) => {
					store.commit('setTooltipObject', null);
					this.clustersIds = this.clustersIds.filter((i) => i !== args.id);
					this.panoramaViewer.removeHotSpot(args.id, this.panoCustomSceneId);
					args.items.forEach((o) => {
						this.addStructuralHotSpot(o);
					});
				},
				clickHandlerArgs: { id: id, items: o, index },
			};

			this.trackAndAddHotspot(hotspotImg, this.panoCustomSceneId);
		});

		items.forEach((o) => {
			this.addStructuralHotSpot(o);
		});
	}

	addStructuralHotSpot(o, options = {}) {
		const image = this.panoramaViewer.forgeImage;
		const position = image.forgeCameraPosition;

		let direction = new THREE.Vector3();
		direction.subVectors(o.point, position).normalize();

		let info = this.forgeService.getNewYawAndPitch(image, direction);
		const isPositioned = !!o.positionImage360Id;
		let hotspotIcon = {
			id: o.id,
			pitch: info['pitch'],
			yaw: info['yaw'],
			cssClass: isPositioned ? 'custom-hotspot positioned-tag-hotspot' : 'custom-hotspot',
			createTooltipFunc: this.createHotspotLabel,
			createTooltipArgs: {
				title: o.name,
				category: isPositioned ? 'Positioned' : o.category,
				className: this.selectedClassName(
					isPositioned ? 'tag-3d-pano positioned-tag-label' : 'tag-3d-pano',
					options,
				),
				imgClass: 'tag-360-hotspot',
				imageName: 'structural',
			},
			clickHandlerFunc: function (event) {
				store.dispatch('FETCH_STRUCTURAL_TAG_BY_ID', o.id).then((tag) => {
					store.state.forgeService.onClickOn3dIcon(
						tag,
						store.state.forgeService.panoEventToContainerOffset(event),
						'360',
					);
				});
			},
			clickHandlerArgs: 'test',
		};

		this.trackAndAddHotspot(hotspotIcon, this.panoCustomSceneId);
	}

	/**
	 * Updates fetched inspections hotspots in the panorama viewer
	 */
	updateInspectionsHotspots() {
		if (!store.state.userSettings.displaySettings.showInspections) {
			return;
		}

		const image = this.panoramaViewer.forgeImage;
		if (!image) return;

		const position = image.forgeCameraPosition;
		if (!position) return;

		let visRange = this.getVisibilityRange();

		let inspections = [];

		if (store.state.TAGS.InspectionSelectedFilter) {
			inspections = store.getters.GET_ALL_AMIS_FILTERED_INSPECTIONS.filter(t => !!t.point);
		} else {
			inspections = store.getters.GET_ALL_AMIS_TAGS_IDS;
		}
		inspections = this.filterOrdinaryTagMarkers(inspections);

		if (this.isGlobalSearchActive) {
			inspections = inspections.filter((o) => this.isolatedTagsIds.has(o.id));
		}

		inspections = this.filterByVisibilityRange(inspections, image.forgeCameraPosition, visRange);

		let { items, clusters } = store.state.forgeService.getTagsClustersAndItems(inspections, position, visRange);

		clusters.forEach((o, index) => {
			var box = new THREE.Box3().setFromPoints(o.map((o) => o.point));
			var center = box.getCenter();
			var direction = new THREE.Vector3();
			direction.subVectors(center, position).normalize();
			var info = this.forgeService.getNewYawAndPitch(image, direction);
			var id = `auto-tag-cluster-${index}`;
			this.clustersIds.push(id);
			let hotspotImg = {
				id: id,
				pitch: info['pitch'],
				yaw: info['yaw'],
				cssClass: 'custom-hotspot',
				createTooltipFunc: this.clusterHotSpot,
				createTooltipArgs: { items: o, position: center, image: image, type: 'inspection' },
				clickHandlerFunc: (event, args) => {
					store.commit('setTooltipObject', null);
					this.clustersIds = this.clustersIds.filter((i) => i !== args.id);
					this.panoramaViewer.removeHotSpot(args.id, this.panoCustomSceneId);
					args.items.forEach((o) => {
						this.addInspectionHotSpot(o);
					});
				},
				clickHandlerArgs: { id: id, items: o, index },
			};

			this.trackAndAddHotspot(hotspotImg, this.panoCustomSceneId);
		});

		items.forEach((o) => {
			this.addInspectionHotSpot(o);
		});

	}

	/**
	 * Adds an inspection hotspot to the panorama viewer
	 * @param {Object} o - The inspection object
	 * @param {THREE.Vector3} position - The position of the hotspot
	 * @param {Object} image - The image object
	 * @param {Function} hotspot - The hotspot tooltip function
	 */
	addInspectionHotSpot(o, options = {}) {
		const image = this.panoramaViewer.forgeImage;
		const position = image.forgeCameraPosition;

		let direction = new THREE.Vector3();
		direction.subVectors(o.point, position).normalize();

		let info = this.forgeService.getNewYawAndPitch(image, direction);

		let hotspotIcon = {
			id: o.id,
			pitch: info['pitch'],
			yaw: info['yaw'],
			cssClass: 'custom-hotspot',
			createTooltipFunc: this.createHotspotLabelByColor.bind(this),
			createTooltipArgs: {
				title: o.name,
				category: o.category,
				className: this.selectedClassName('annotation-3d-pano', options),
				imgClass: 'inspection-360-hotspot',
				imageName: 'inspection',
				color: store.getters.GET_SELECTED_COLORS_OPTIONS?.length ? o.pinColor : TAG_COLOR_HEX.Inspection,
			},
			clickHandlerFunc: function (event) {
				store.dispatch('FETCH_INSPECTION_TAG_BY_ID', o.id).then((tag) => {
					store.state.forgeService.onClickOn3dIcon(tag, store.state.forgeService.panoEventToContainerOffset(event), '360');
				});
			},
		};

		this.trackAndAddHotspot(hotspotIcon, this.panoCustomSceneId);
	}


	/**
	 * Filters elements by visibility range
	 * @param {Array} elements - Elements to filter
	 * @param {THREE.Vector3} cameraPosition - Camera position
	 * @param {number} visRange - Visibility range
	 * @returns {Array} Filtered elements
	 */
	filterByVisibilityRange(elements, cameraPosition, visRange) {
		const withDist = elements
			.filter(el => el.point)
			.map(el => ({ el, dist: el.point.distanceTo(cameraPosition) }))
			.sort((a, b) => a.dist - b.dist);

		const filtered = withDist.filter(({ dist }) => dist < visRange).map(({ el }) => el);

		return filtered;
	}

	updateReferencePointHotSpot() {
		if (!this.panoramaViewer) return;

		this.panoramaViewer.removeHotSpot(this.REFERENCE_POINT_ID, this.panoCustomSceneId);

		const referencePoint = store.state.referencePoint360;

		if (!referencePoint) return;

		const direction = new THREE.Vector3().subVectors(referencePoint, this.panoramaViewer.forgeImage.forgeCameraPosition).normalize();
		const info = this.forgeService.getNewYawAndPitch(this.panoramaViewer.forgeImage, direction);
		const pitch = info['pitch'];
		const yaw = info['yaw'];

		const hotspotIcon = {
			id: this.REFERENCE_POINT_ID,
			pitch: pitch,
			yaw: yaw,
			cssClass: 'custom-hotspot',
			createTooltipFunc: this.createReferencePointHotspotLabel.bind(this)
		};

		this.trackAndAddHotspot(hotspotIcon, this.panoCustomSceneId);
	}

	createReferencePointHotspotLabel(hotSpotDiv, args) {

		const imagePath = require(`@/images/tags-icons/default.png`);

		const label = document.createElement('div');
		label.className = 'tag-3d-pano reference-point-hotspot selected';

		const icon = document.createElement('img');
		icon.className = 'tag-360 tag-360-hotspot';
		icon.src = imagePath;

		label.addEventListener('dblclick', (event) => {
			store.state.referencePoint360 = null;
			this.panoramaViewer.removeHotSpot(this.REFERENCE_POINT_ID, this.panoCustomSceneId);
		});

		label.appendChild(icon);
		hotSpotDiv.appendChild(label);
	}

	/**
	 * Adds a generic hotspot to the panorama viewer
	 * @param {Object} item - Item to add hotspot for
	 * @param {Object} image - Panorama image
	 * @param {string} className - CSS class name
	 * @param {string} imgClass - Image class name
	 * @param {string} imageName - Image name
	 * @param {Function} clickHandler - Click handler function
	 */
	addGenericHotspot(item, image, className, imgClass, imageName, clickHandler) {
		const direction = new THREE.Vector3().subVectors(item.point, image.forgeCameraPosition).normalize();
		const info = this.forgeService.getNewYawAndPitch(image, direction);

		const hotspotIcon = {
			id: item.id,
			pitch: info['pitch'],
			yaw: info['yaw'],
			cssClass: 'custom-hotspot',
			createTooltipFunc: this.createHotspotLabel,
			createTooltipArgs: {
				title: item.name,
				category: item.category,
				className: className,
				imgClass: imgClass,
				imageName: imageName,
			},
			clickHandlerFunc: clickHandler,
			clickHandlerArgs: 'test',
		};

		this.trackAndAddHotspot(hotspotIcon, this.panoCustomSceneId);
	}

	/**
	 * Creates a cluster hotspot for grouped items
	 * @param {HTMLElement} hotSpotDiv - The hotspot div element
	 * @param {Object} args - Arguments for the hotspot label
	 */
	clusterHotSpot(hotSpotDiv, args) {
		let image = args.image;

		let imageName = args.type || 'default';

		var imagePath = require(`@/images/tags-icons/${imageName}-hover.png`);

		hotSpotDiv.classList.add('custom-tooltip');
		var personIcon = document.createElement('img');
		personIcon.className = `person-icon cluster-icon`;
		personIcon.src = imagePath;
		hotSpotDiv.appendChild(personIcon);
		//All calculated in meters
		var devicePixelRatio = window.devicePixelRatio;
		let unitScale = store.state.unitScale ?? 1.0;
		var scaleFactor = 1 / unitScale;
		var distanceFromCamera = args.position.distanceTo(image.forgeCameraPosition) * scaleFactor * 2;
		let newWidth = 30.0 * (30.0 / (distanceFromCamera + 1.0));
		if (newWidth < 40) newWidth = 40;

		// Max gl_PointSize value
		if (newWidth > 1024.0) newWidth = 1024.0;
		newWidth /= devicePixelRatio;

		personIcon.style.width = newWidth + 'px';
		personIcon.style.height = newWidth + 'px';

		personIcon.addEventListener('mouseover', function (e) {
			let personIconBoundry = personIcon.getBoundingClientRect();

			let width = personIconBoundry.width ? personIconBoundry.width : 20; // personIcon css class default width
			var hotSpotDivStyle = window.getComputedStyle(hotSpotDiv);
			var matrix = new window.WebKitCSSMatrix(hotSpotDivStyle.transform);

			const text = `Count: ${args.items.length ?? 0}`;
			const font = '16px ArtifaktElement';
			const canvas = document.createElement('canvas');
			const context = canvas.getContext('2d');
			context.font = font;
			const offset = context.measureText(text);

			let textWidth = offset.width > 200 ? offset.width : 200;

			store.commit('setTooltipObject', {
				title: `Count: ${args.items.length ?? 0}`,
				position: {
					left: matrix.m41,
					top: matrix.m42 - 40,
				},
				margin: width / 2 - (textWidth + 20) / 2, // tooltip width + 20 padding
			});
		});

		personIcon.addEventListener('mouseout', function (e) {
			store.commit('setTooltipObject', null);
		});
	}

	/**
	 * Creates an HTML label for a hotspot from a PNG icon.
	 * Shares the chip/title chrome with createHotspotLabelByColor so both builders
	 * render one consistent label; the tag colour is carried by the PNG icon.
	 * @param {HTMLElement} hotSpotDiv - The hotspot div element
	 * @param {Object} args - Arguments for the hotspot label
	 */
	createHotspotLabel(hotSpotDiv, args) {
		var imagePath = require(`@/images/tags-icons/${args.imageName}-hover.png`);

		var label = document.createElement('div');
		label.className = args.className;
		label.style.backgroundColor = HOTSPOT_LABEL_CHIP_BG;

		var icon = document.createElement('img');
		icon.className = 'tag-360 ' + args.imgClass;
		icon.src = imagePath;
		label.appendChild(icon);

		var textDiv = document.createElement('div');
		textDiv.innerText = args.title;
		textDiv.className = 'tag-title';
		textDiv.style.color = HOTSPOT_LABEL_TEXT_COLOR;
		textDiv.style.textShadow = HOTSPOT_LABEL_TEXT_SHADOW;
		label.appendChild(textDiv);

		if (args.category) store.state.forgeService.addCategoryColorToLabel(args.category, label);
		hotSpotDiv.appendChild(label);
	}

	/**
	 * Shows CML hotspots in the 360 panorama view
	 * CML hotspots have a separate lifecycle from tag hotspots (managed by inspectionEdit/Info mount/destroy)
	 * @param {Object} inspection - The inspection with cmlRecords
	 */
	showCmlHotspots(inspection) {
		if (!inspection?.cmlRecords) return;

		// Store reference BEFORE viewer guard for async 360 load
		this._lastCmlInspection = inspection;

		if (!this.panoramaViewer) return;

		// Clear previous CML hotspots first. NOTE: clearCmlHotspots() nulls
		// _lastCmlInspection, so re-set it immediately — otherwise the
		// image-change re-render hook in updateAllHotspots() no-ops and CML
		// hotspots vanish when panning to a different 360 scan image.
		this.clearCmlHotspots();
		this._lastCmlInspection = inspection;

		const image = this.panoramaViewer.forgeImage;
		if (!image) return;

		const position = image.forgeCameraPosition;
		if (!position) return;

		inspection.cmlRecords
			.filter(cml => cml.point)
			.forEach(cml => {
				const pt = cml.point;
				const worldPoint = new THREE.Vector3(pt.x, pt.y, pt.z);

				const direction = new THREE.Vector3().subVectors(worldPoint, position).normalize();
				const info = this.forgeService.getNewYawAndPitch(image, direction);

				const conditionColor = this.forgeService._getCmlConditionColor(cml);
				// Phase 1F: `remainingThickness` dropped — route through server-computed cache.
				const labelText = cml.cmlNo
					? cml.cmlNo + (cml.currentMinWT_Cached != null ? ` | ${cml.currentMinWT_Cached.toFixed(2)}mm` : '')
					: 'CML';

				const hotspotId = `cml-360-${cml.id || cml.cmlNo || Math.random()}`;

				// Captured when Pannellum renders the hotspot (createTooltipFunc),
				// then used as the popup anchor in the click handler below.
				let hotspotEl = null;

				const hotspotConfig = {
					id: hotspotId,
					pitch: info['pitch'],
					yaw: info['yaw'],
					cssClass: 'custom-hotspot',
					createTooltipFunc: (hotSpotDiv, args) => {
						hotspotEl = hotSpotDiv;
						this.createCmlHotspotLabel(hotSpotDiv, args);
					},
					createTooltipArgs: {
						label: labelText,
						color: conditionColor,
					},
					// Open the CML popup on click — parity with the 3D marker path
					// (forgeService._setupCmlMarkerClickHandler → openPopupCard).
					// Every other 360 hotspot type already supplies a
					// clickHandlerFunc; the CML hotspot was the only one missing it,
					// so clicks did nothing. The popup's rAF positioner tracks
					// `hotspotEl`'s rect, so the card follows the hotspot as the user
					// pans (and auto-closes when it leaves the viewport, same as 3D).
					clickHandlerFunc: () => {
						openPopupCard('cml', cml.id, hotspotEl, { sourceViewer: '360' });
					},
				};

				this.panoramaViewer.addHotSpot(hotspotConfig, this.panoCustomSceneId);
				this.cmlHotspotIds.push(hotspotId);
			});
	}

	/**
	 * Clears all CML hotspots from the 360 panorama
	 */
	clearCmlHotspots() {
		if (!this.panoramaViewer) return;

		this.cmlHotspotIds.forEach(id => {
			this.panoramaViewer.removeHotSpot(id, this.panoCustomSceneId);
		});
		this.cmlHotspotIds = [];
		this._lastCmlInspection = null;
	}

	/**
	 * @returns {boolean} whether a CML hotspot set is currently registered for the
	 * 360 panorama (set by showCmlHotspots, nulled by clearCmlHotspots). The popup's
	 * show-in-360 fallback checks this to avoid clobbering a tag-panel-rendered set.
	 */
	hasCmlHotspots() {
		return this._lastCmlInspection != null;
	}

	/**
	 * Creates an HTML label for a CML hotspot in 360 view
	 * Displays as a compact pill badge with condition color
	 * @param {HTMLElement} hotSpotDiv - The hotspot div element
	 * @param {Object} args - { label: string, color: string }
	 */
	createCmlHotspotLabel(hotSpotDiv, args) {
		// Marker-at-point + label-below (mirrors the 3D viewer: a condition-colored
		// shape sits ON the point, the text floats beneath). The wrapper must size to
		// the MARKER only — the label is position:absolute (out of flow) — so
		// Pannellum, which centers the hotspot div by its offset size, anchors the
		// SHAPE on the yaw/pitch point. See CML_TML_MARKER_SHAPE_UNIFICATION.md §4B.
		const wrapper = document.createElement('div');
		wrapper.className = 'cml-360-hotspot';

		const marker = document.createElement('span');
		marker.className = 'cml-360-marker';       // clip-path triangle, condition-colored
		marker.style.backgroundColor = args.color;
		wrapper.appendChild(marker);

		const label = document.createElement('span');
		label.className = 'cml-360-label';
		label.style.backgroundColor = args.color; // full condition color — unified with the 3D label
		label.textContent = args.label;
		wrapper.appendChild(label);

		hotSpotDiv.appendChild(wrapper);
	}

	/**
	 * Shows TML (sub-point) hotspots in the 360 panorama view.
	 *
	 * TmlPoints are children of CmlRecords (D14 hierarchy: condition judgment
	 * lives on the CML; TMLs are the measurement contributors). They render as
	 * smaller square markers (`.tml-360-marker`) so they read as subordinate to
	 * the CML triangles — mirroring the 3D viewer's square(TML)-vs-triangle(CML)
	 * distinction. Lifecycle parity with showCmlHotspots: separate ID tracking,
	 * re-rendered on 360 image change via updateAllHotspots(), and gated upstream
	 * by `showTml` (the forgeService.showTmlPointMarkers caller only reaches here
	 * after its toggle gate, so these hotspots inherit the toggle state).
	 *
	 * @param {Object|Array} input - `{ cmlRecords: [...] }` or a bare CmlRecord[],
	 *   each CML carrying a populated `tmlPoints` array. Same shape the 3D
	 *   forgeService.showTmlPointMarkers accepts.
	 */
	showTmlHotspots(input) {
		const cmlRecords = Array.isArray(input?.cmlRecords)
			? input.cmlRecords
			: (Array.isArray(input) ? input : null);
		if (!cmlRecords) return;

		// Store reference BEFORE the viewer guard so a render requested before
		// the pano viewer exists is replayed once the panorama loads (parity
		// with showCmlHotspots).
		this._lastTmlInput = input;

		if (!this.panoramaViewer) return;

		// Clear previous TML hotspots first. clearTmlHotspots() nulls
		// _lastTmlInput, so re-set it immediately (same fix as the CML path).
		this.clearTmlHotspots();
		this._lastTmlInput = input;

		const image = this.panoramaViewer.forgeImage;
		if (!image) return;

		const position = image.forgeCameraPosition;
		if (!position) return;

		cmlRecords.forEach(cml => {
			if (!cml?.tmlPoints || !Array.isArray(cml.tmlPoints)) return;

			// Color inherited from the parent CML — TMLs carry no condition
			// judgment of their own (D14).
			const conditionColor = this.forgeService._getCmlConditionColor(cml);

			cml.tmlPoints
				.filter(tp => tp && tp.point && !tp.isDeleted)
				.forEach(tp => {
					const pt = tp.point;
					const worldPoint = new THREE.Vector3(pt.x, pt.y, pt.z);

					const direction = new THREE.Vector3().subVectors(worldPoint, position).normalize();
					const info = this.forgeService.getNewYawAndPitch(image, direction);

					// Free-text position label per D11 (e.g. "NORTH"); fall back to
					// "TML" so an unlabeled point still gets a readable pill.
					const labelText = tp.position || 'TML';

					const hotspotId = `tml-360-${tp.id || Math.random()}`;

					// Captured when Pannellum renders the hotspot (createTooltipFunc),
					// then used as the popup anchor in the click handler below.
					let hotspotEl = null;

					const hotspotConfig = {
						id: hotspotId,
						pitch: info['pitch'],
						yaw: info['yaw'],
						cssClass: 'custom-hotspot',
						createTooltipFunc: (hotSpotDiv, args) => {
							hotspotEl = hotSpotDiv;
							this.createTmlHotspotLabel(hotSpotDiv, args);
						},
						createTooltipArgs: {
							label: labelText,
							color: conditionColor,
						},
						// Open the TmlPoint popup on click — parity with the CML
						// hotspot and the 3D TML marker path (forgeService
						// ._setupTmlMarkerClickHandler). parentCmlId is a free hint
						// (we have the parent in scope) that lets the popup skip an
						// O(allCmls) cache scan / lazy-load when cold.
						clickHandlerFunc: () => {
							openPopupCard('tmlpoint', tp.id, hotspotEl, { parentCmlId: cml.id, sourceViewer: '360' });
						},
					};

					this.panoramaViewer.addHotSpot(hotspotConfig, this.panoCustomSceneId);
					this.tmlHotspotIds.push(hotspotId);
				});
		});
	}

	/**
	 * Clears all TML hotspots from the 360 panorama
	 */
	clearTmlHotspots() {
		if (!this.panoramaViewer) return;

		this.tmlHotspotIds.forEach(id => {
			this.panoramaViewer.removeHotSpot(id, this.panoCustomSceneId);
		});
		this.tmlHotspotIds = [];
		this._lastTmlInput = null;
	}

	/**
	 * Creates an HTML label for a TML hotspot in 360 view.
	 * Subordinated styling vs CML: a smaller square marker + label-below, to
	 * echo the 3D viewer's square(TML)-vs-triangle(CML) visual language.
	 * @param {HTMLElement} hotSpotDiv - The hotspot div element
	 * @param {Object} args - { label: string, color: string }
	 */
	createTmlHotspotLabel(hotSpotDiv, args) {
		// Marker-at-point + label-below (square marker; TML is subordinate to the CML
		// triangle per D14). Same anchoring contract as the CML builder — the label
		// MUST stay position:absolute so the wrapper sizes to the square and Pannellum
		// centers the square on the point.
		const wrapper = document.createElement('div');
		wrapper.className = 'tml-360-hotspot';

		const marker = document.createElement('span');
		marker.className = 'tml-360-marker';       // square, condition-colored (inherited from parent CML)
		marker.style.backgroundColor = args.color;
		wrapper.appendChild(marker);

		const label = document.createElement('span');
		label.className = 'tml-360-label';
		label.style.backgroundColor = args.color; // full condition color — unified with the 3D label
		label.textContent = args.label;
		wrapper.appendChild(label);

		hotSpotDiv.appendChild(wrapper);
	}

	/**
	 * Adds a hotspot to the panorama viewer and tracks its ID
	 * @param {Object} hotspotConfig - The hotspot configuration
	 * @param {string} sceneId - The scene ID
	 */
	trackAndAddHotspot(hotspotConfig, sceneId) {
		// Add the hotspot to the panorama viewer
		this.panoramaViewer.addHotSpot(hotspotConfig, sceneId);

		// Track the hotspot ID for efficient clearing later
		if (hotspotConfig.id != null
			&& !this.activeHotspotIds.some(id => String(id) === String(hotspotConfig.id))) {
			this.activeHotspotIds.push(hotspotConfig.id);
		}
	}
}
